'use client';

import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBodyOverflow } from '@/hooks/use-body-overflow';

interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  side?: 'left' | 'right';
}

export function Drawer({ open, onOpenChange, children, side = 'right' }: DrawerProps) {
  // Lock body scroll when drawer is open
  useBodyOverflow(open);

  if (!open) return null;

  return (
    <>
      {/* Overlay - starts below header (h-14 = 3.5rem) */}
      <div
        className="fixed top-14 left-0 right-0 bottom-0 z-50 bg-black/50 backdrop-blur-md"
        onClick={() => onOpenChange(false)}
      />
      {/* Drawer - aligned with overlay, starts below header */}
      <div
        className={cn(
          'fixed top-14 left-0 right-0 z-50 h-[calc(100vh-3.5rem)] w-full max-w-full bg-background shadow-xl overflow-y-auto sm:left-auto sm:right-0 sm:w-[90vw] sm:max-w-[600px] lg:w-[40vw] lg:max-w-[800px]',
          side === 'left'
            ? 'sm:left-0 sm:right-auto border-t sm:border-t-0 sm:border-r animate-slide-in-left'
            : 'sm:right-0 sm:left-auto border-t sm:border-t-0 sm:border-l animate-slide-in-right'
        )}
      >
        {children}
      </div>
    </>
  );
}

export function DrawerHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col space-y-1.5 p-6 border-b border-border', className)}
      {...props}
    />
  );
}

export function DrawerTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn('text-2xl font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  );
}

export function DrawerDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

export function DrawerContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-6 space-y-4', className)} {...props} />;
}

export function DrawerClose({
  className,
  onClick,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none text-muted-foreground hover:text-foreground',
        className
      )}
      onClick={onClick}
      {...props}
    >
      <X className="h-5 w-5" />
      <span className="sr-only">Close</span>
    </button>
  );
}
